import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  VideoFile,
  Image,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { MediaData } from '../../types';
import { createMedia, updateMedia } from '../../store/slices/mediaSlice';

interface MediaFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  media?: MediaData | null;
}

const schema = yup.object({
  type: yup.string().oneOf(['creation', 'starting', 'ending']).required('Type is required'),
  isVideo: yup.boolean().required('Format is required'),
});

interface MediaFormData {
  type: 'creation' | 'starting' | 'ending';
  isVideo: boolean;
  file?: File;
}

const MediaFormModal: React.FC<MediaFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  media,
}) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<MediaFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: 'creation',
      isVideo: false,
    },
  });

  const isVideo = watch('isVideo');

  useEffect(() => {
    if (open) {
      if (media) {
        setValue('type', media.type);
        setValue('isVideo', media.isVideo);
      } else {
        reset();
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    }
  }, [open, media, reset, setValue]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Auto-detect if it's a video
      const isVideoFile = file.type.startsWith('video/');
      setValue('isVideo', isVideoFile);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const onSubmit = async (data: MediaFormData) => {
    if (!selectedFile && !media) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      if (media) {
        // Update existing media
        const result = await dispatch(updateMedia({
          mediaId: media.id,
          mediaData: {
            type: data.type,
            isVideo: data.isVideo,
          }
        }) as any);
        
        if (result.payload?.success) {
          toast.success('Media updated successfully');
          onSuccess();
        } else {
          toast.error('Failed to update media');
        }
      } else {
        // Create new media
        const result = await dispatch(createMedia({
          file: selectedFile!,
          isVideo: data.isVideo,
          type: data.type,
        }) as any);
        
        if (result.payload?.success) {
          toast.success('Media uploaded successfully');
          onSuccess();
        } else {
          toast.error('Failed to upload media');
        }
      }
    } catch (error) {
      toast.error('An error occurred while processing media');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {media ? 'Edit Media' : 'Upload Media'}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          {!media && (
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                border: '2px dashed #ccc',
                cursor: 'pointer',
                mb: 3,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {selectedFile ? (
                <Box>
                  {previewUrl && (
                    <Box sx={{ mb: 2 }}>
                      {isVideo ? (
                        <video
                          src={previewUrl}
                          style={{ maxWidth: '100%', maxHeight: 200 }}
                          controls
                        />
                      ) : (
                        <img
                          src={previewUrl}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
                        />
                      )}
                    </Box>
                  )}
                  <Typography variant="body1" gutterBottom>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                  >
                    Remove File
                  </Button>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Click to upload media
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supports images and videos
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2 
          }}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Type</InputLabel>
                  <Select {...field} label="Type">
                    <MenuItem value="creation">Creation</MenuItem>
                    <MenuItem value="starting">Starting</MenuItem>
                    <MenuItem value="ending">Ending</MenuItem>
                  </Select>
                  {errors.type && (
                    <Typography variant="caption" color="error">
                      {errors.type.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="isVideo"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.isVideo}>
                  <InputLabel>Format</InputLabel>
                  <Select
                    {...field}
                    label="Format"
                    disabled={!!selectedFile} // Auto-detect from file
                    value={field.value ? 'true' : 'false'}
                    onChange={(e) => field.onChange(e.target.value === 'true')}
                  >
                    <MenuItem value="false">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Image />
                        Image
                      </Box>
                    </MenuItem>
                    <MenuItem value="true">
                      <Box display="flex" alignItems="center" gap={1}>
                        <VideoFile />
                        Video
                      </Box>
                    </MenuItem>
                  </Select>
                  {errors.isVideo && (
                    <Typography variant="caption" color="error">
                      {errors.isVideo.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={loading || (!selectedFile && !media)}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Processing...' : (media ? 'Update' : 'Upload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaFormModal;
